import { getCookie, setCookie } from './cookies';
import { APP_NAME } from '../configs/constants';

const getLog = Symbol();
const getType = Symbol();

enum LEVEL_ENUM {
    LEVEL_LOG = 2,
    LEVEL_ERROR = 3,
    LEVEL_WARN = 4,
    LEVEL_INFO = 6,
    LEVEL_DEBUG = 7,
    LEVEL_SUCCESS = 8,
    LEVEL_GROUP = 0,
}

export default class Logger {
    private _additionMessage: string | undefined;
    level: LEVEL_ENUM;
    private isDebug: boolean;

    constructor() {
        this.level = LEVEL_ENUM.LEVEL_INFO;
        this.isDebug = Logger._isDebug();
    }

    setLevel(level: LEVEL_ENUM) {
        this.level = level;
    }

    get additionMessage() {
        return this._additionMessage;
    }

    set additionMessage(additionMessage) {
        this._additionMessage = additionMessage;
    }

    static _isDebug(): boolean {
        const urlSearchParam = window.location.search;
        const isCookieEnable = window.Inpl.Ad.detection?.isCookieEnable;
        const searchedUANCParam = 'uanc_debug';

        if (isCookieEnable && getCookie(searchedUANCParam)) return true;
        if (!urlSearchParam) return false;

        const params = urlSearchParam.slice(1, urlSearchParam.length).split('&');

        return !!params.find((p) => {
            if (p === searchedUANCParam) {
                if (isCookieEnable) {
                    setCookie({ name: searchedUANCParam, value: true, days: 1 });
                }
                return true;
            }
            return false;
        });
    }

    log(...args: any[]) {
        this[getLog](LEVEL_ENUM.LEVEL_LOG, args);
    }

    error(...args: any[]) {
        this[getLog](LEVEL_ENUM.LEVEL_ERROR, args);
    }

    warn(...args: any[]) {
        this[getLog](LEVEL_ENUM.LEVEL_WARN, args);
    }

    info(...args: any[]) {
        this[getLog](LEVEL_ENUM.LEVEL_INFO, args);
    }

    table(label: string, obj: any) {
        this.group(label);
        console.table(obj);
        console.groupEnd();
    }

    group(...args: any[]) {
        this[getLog](LEVEL_ENUM.LEVEL_GROUP, args);
    }

    debug(...args: any[]) {
        this[getLog](LEVEL_ENUM.LEVEL_DEBUG, args);
    }

    success(...args: any[]) {
        this[getLog](LEVEL_ENUM.LEVEL_SUCCESS, args);
    }

    [getLog](level: LEVEL_ENUM, args: any[]) {
        let text = args.join(': ');
        if (!this.isDebug) return false;

        if (this.additionMessage) {
            text = `${this.additionMessage}: ${text}`;
        }

        let method: keyof Console = this[getType](level);

        let styles = 'color: #fff; background-color: #000';

        if (method === 'error') {
            styles = 'color: #fff; background-color: #f16565';
        } else if (method === 'warn') {
            styles = 'color: #000; background-color: orange';
        } else if (method === 'info') {
            styles = 'color: #fff; background-color: blue';
        } else if (level === LEVEL_ENUM.LEVEL_SUCCESS) {
            styles = 'color: #000; background-color: #2ba637';
        } else if (method === 'log') {
            styles = 'color: #000; background-color: #ffc175';
        }

        if (level === LEVEL_ENUM.LEVEL_SUCCESS) {
            method = 'log';
        }

        (console[method] as (...data: any[]) => void)(
            `%c${APP_NAME.toUpperCase()}%c${text}`,
            `${styles}; padding: 5px;`,
            'color: white; padding-left: 5px;'
        );
    }

    [getType](level: LEVEL_ENUM): keyof Console {
        let type: string;

        switch (level) {
            case LEVEL_ENUM.LEVEL_ERROR:
                type = 'error';
                break;
            case LEVEL_ENUM.LEVEL_INFO:
                type = 'info';
                break;
            case LEVEL_ENUM.LEVEL_DEBUG:
                type = 'debug';
                break;
            case LEVEL_ENUM.LEVEL_WARN:
                type = 'warn';
                break;
            case LEVEL_ENUM.LEVEL_GROUP:
                type = 'groupCollapsed';
                break;
            case LEVEL_ENUM.LEVEL_SUCCESS:
                type = 'success';
                break;
            default:
                type = 'log';
        }
        return type as keyof Console;
    }
}
